import type { DesignTokens, ThemeRow, ThemeTokens } from "../types/theme";
import { DARK_BASE_TOKENS, LIGHT_BASE_TOKENS } from "../constants/theme-tokens";
import type { TypographyContextValue } from "../types/typography";
import { resolveThemeTokens } from "./theme-resolver";

/**
 * Modo de color CANÓNICO elegido por el usuario. Es ORTOGONAL al tema:
 *  - "system": la variante clara/oscura sigue al SO (prefers-color-scheme).
 *  - "light"/"dark": override EXPLÍCITO que GANA sobre el SO.
 * Se persiste por dispositivo (localStorage). Un tema personalizado tiene paleta
 * clara y oscura; el modo selecciona CUÁL se aplica, sin cambiar el tema activo.
 */
export type ColorMode = "system" | "light" | "dark";

/** Variante efectiva ya resuelta (nunca "system"). */
export type EffectiveMode = "light" | "dark";

/** Id sentinela del tema por defecto (no personalizado). */
export const DEFAULT_THEME_ID = "default";

export interface ResolveThemeInput {
  /**
   * Fila del tema personalizado global activo (con su par claro/oscuro), o `null`
   * para el tema POR DEFECTO. Una fila sin `id` se trata como por defecto.
   */
  themeRow: ThemeRow | null;
  /** Modo canónico elegido por el usuario. */
  mode: ColorMode;
  /** Si el SO reporta esquema oscuro (`prefers-color-scheme: dark`). */
  systemPrefersDark: boolean;
  isBlurContext?: boolean;
  parentTokens?: ThemeTokens | null;
  userFonts?: TypographyContextValue;
}

export interface ResolveThemeResult {
  /** Id del tema aplicado (`"default"` o el id del tema personalizado). */
  themeId: string;
  /** True cuando se aplica el tema por defecto (no personalizado). */
  isDefault: boolean;
  /** Variante que realmente se aplica (clara u oscura). */
  effectiveMode: EffectiveMode;
  /** Tokens resueltos listos para aplicar al DOM. */
  tokens: ThemeTokens;
}

/**
 * Resuelve la variante efectiva: el modo EXPLÍCITO (light/dark) GANA sobre el SO.
 * Solo en "system" se sigue `prefers-color-scheme`.
 */
export function resolveEffectiveMode(mode: ColorMode, systemPrefersDark: boolean): EffectiveMode {
  if (mode === "light") {
    return "light";
  }
  if (mode === "dark") {
    return "dark";
  }
  return systemPrefersDark ? "dark" : "light";
}

function baseThemeTokens(dark: boolean, isBlur: boolean): ThemeTokens {
  const base: DesignTokens = dark ? DARK_BASE_TOKENS : LIGHT_BASE_TOKENS;
  return { ...base, isDark: dark, isBlur, id: DEFAULT_THEME_ID };
}

/**
 * Lógica PURA de resolución del sistema de temas (dos ejes ortogonales):
 *   (a) QUÉ tema  → `themeRow` (null = por defecto, o un tema personalizado)
 *   (b) QUÉ modo  → `mode` (system/light/dark) → variante clara/oscura
 *
 * Reglas garantizadas (y cubiertas por tests):
 *  - El modo EXPLÍCITO (light/dark) SIEMPRE gana sobre el SO. Solo "system" sigue al SO.
 *  - Un tema personalizado se CONSERVA al cambiar de modo; el modo solo elige su
 *    variante clara u oscura (usando el par claro/oscuro del tema si existe).
 *  - El tema por defecto es una selección más (themeRow = null).
 */
export function resolveTheme(input: ResolveThemeInput): ResolveThemeResult {
  const { themeRow, mode, systemPrefersDark } = input;
  const isBlurContext = input.isBlurContext ?? true;
  const effectiveMode = resolveEffectiveMode(mode, systemPrefersDark);
  const wantDark = effectiveMode === "dark";

  // ── Tema por defecto (sin personalizado; fila global sin id = por defecto) ──
  if (!themeRow?.id) {
    return {
      themeId: DEFAULT_THEME_ID,
      isDefault: true,
      effectiveMode,
      tokens: baseThemeTokens(wantDark, isBlurContext),
    };
  }

  // ── Tema personalizado: elegir la variante que coincide con el modo efectivo ─
  const primaryIsDark = themeRow.is_dark === 1;
  const hasPair = Boolean(themeRow.p_id);
  const pairMatchesMode = hasPair && (themeRow.p_is_dark === 1) === wantDark;
  // El modo puede honrarse si la variante primaria o su par coincide con él.
  const canHonorMode = primaryIsDark === wantDark || pairMatchesMode;

  // Cuando el modo puede honrarse forzamos follow_device_theme=1 con
  // systemIsDark=wantDark: así el resolver elige la variante correcta (primaria o
  // par) SIN depender de la preferencia follow-device del usuario. Cuando NO puede
  // (tema de una sola variante forzado al modo contrario), resolvemos el tema en su
  // propia variante (follow_device_theme=0) para no mezclar isDark con colores.
  const tokens = resolveThemeTokens({
    themeResult: { ...themeRow, follow_device_theme: canHonorMode ? 1 : 0 },
    isBlurContext,
    systemIsDark: wantDark,
    parentTokens: input.parentTokens ?? null,
    userFonts: input.userFonts,
  });

  return {
    themeId: tokens.id ?? themeRow.id,
    isDefault: false,
    effectiveMode: tokens.isDark ? "dark" : "light",
    tokens,
  };
}
